import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Box, Button, ReplayOutlinedIcon, Skeleton, Text } from '@/ui/primitives';
import type { SearchTagsDateHeaderFiltersProps } from './SearchTagsDateHeaderFilters';
import type { QualityRange } from '@/features/quality/hooks';
import type { TagColor } from '@/shared/data/tags';
import type { ReactElement } from 'react';

type ToolbarDropdownOption = { value: string; label: string; count?: number; color?: TagColor };
type ToolbarFilterDropdownId = 'suite' | 'entity' | 'item' | 'project' | 'status' | 'tags' | 'date';
type DateDropdownOption = { value: QualityRange | 'custom'; label: string };

const defaultDateDropdownOptions = [
  { value: 'latest', label: 'Latest' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '14d', label: 'Last 14 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
] satisfies DateDropdownOption[];

type SearchTagsDateHeaderFiltersNextProps = Omit<
  SearchTagsDateHeaderFiltersProps,
  'projectOptions' | 'statusOptions' | 'tagOptions'
> & {
  tagOptions: ToolbarDropdownOption[];
  projectOptions?: ToolbarDropdownOption[];
  statusOptions?: ToolbarDropdownOption[];
  selectedSuites?: string[];
  onSuitesChange?: (nextSuites: string[]) => void;
  suiteOptions?: ToolbarDropdownOption[];
  suiteLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (nextEntities: string[]) => void;
  entityOptions?: ToolbarDropdownOption[];
  entityLabel?: string;
  selectedItems?: string[];
  onItemsChange?: (nextItems: string[]) => void;
  itemOptions?: ToolbarDropdownOption[];
  itemLabel?: string;
  summaryCount?: number | null;
  summaryStartDay?: string | null;
  summaryEndDay?: string | null;
  summarySingularLabel?: string;
  summaryPluralLabel?: string;
  summaryRelatedEntityLabel?: string | null;
  summaryEmptyText?: string | null;
  summaryLoading?: boolean;
  dateQuickFilterOptions?: QualityRange[];
  showCustomDateRange?: boolean;
  onCustomDateRangeOpen?: (anchorEl: HTMLElement | null) => void;
};

const toolbarControlHeight = 40;
const toolbarControlRadius = r('sm');
const toolbarControlFontSize = 16;
const toolbarPopoverFontSize = 18;
const summarySkeletonWidth = 'min(100%, 760px)';
const summarySlotOffset = s('sm');
const summarySlotBottomCompensation = s('md');

const formatComparisonPeriod = (dayCount: number): string => {
  if (dayCount % 7 === 0) {
    const weekCount = dayCount / 7;
    return `${weekCount.toLocaleString()} week`;
  }

  return `${dayCount.toLocaleString()} day`;
};

const nextToolbarControlSx = {
  '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
    minHeight: `${toolbarControlHeight}px`,
    height: `${toolbarControlHeight}px`,
    borderRadius: toolbarControlRadius,
    backgroundColor: 'background.paper',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: toolbarControlRadius,
  },
  '& .MuiInputAdornment-positionStart .MuiSvgIcon-root': {
    width: 22,
    height: 22,
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: s('none'),
    marginRight: s('sm'),
  },
  '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiInputBase-input, & .MuiOutlinedInput-input, & input':
    {
      fontSize: `${toolbarControlFontSize}px`,
    },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
    {
      fontSize: `${toolbarControlFontSize}px`,
    },
} as const;

const nextFilterBackgroundColor = 'background.paper';
const nextFilterDropdownBackgroundColor = 'background.paper';
const nextFilterInputLeftPadding = s('md');

const toToolbarEmptyLabel = (label: string): string => {
  if (label.endsWith('us')) return `${label}es`;
  if (/[b-df-hj-np-tv-z]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  if (label.endsWith('s')) return label;
  return `${label}s`;
};

const formatSummaryDay = (day: string | null | undefined): string | null => {
  if (!day) return null;
  const parsedDay = dayjs(day);
  return parsedDay.isValid() ? parsedDay.format('MMM D, YYYY') : null;
};

const getSummaryDayCount = (
  startDay: string | null | undefined,
  endDay: string | null | undefined,
): number | null => {
  if (!startDay || !endDay) return null;
  const parsedStartDay = dayjs(startDay);
  const parsedEndDay = dayjs(endDay);
  if (!parsedStartDay.isValid() || !parsedEndDay.isValid()) return null;
  return Math.max(parsedEndDay.diff(parsedStartDay, 'day') + 1, 1);
};

export const SearchTagsDateHeaderFiltersNext = ({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  selectedTags,
  onTagsChange,
  tagOptions,
  tagOptionsLoading,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedItems,
  onItemsChange,
  itemOptions,
  itemLabel = 'Item',
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  range,
  onRangeChange,
  hasCustomDates,
  isLatestMode,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  includeLatest = true,
  actions,
  summaryCount = null,
  summaryStartDay = null,
  summaryEndDay = null,
  summarySingularLabel = 'run',
  summaryPluralLabel = 'runs',
  summaryRelatedEntityLabel = null,
  summaryEmptyText = null,
  summaryLoading = false,
  dateQuickFilterOptions,
  showCustomDateRange = true,
  onCustomDateRangeOpen,
}: SearchTagsDateHeaderFiltersNextProps): ReactElement => {
  const dateControlRef = useRef<HTMLDivElement | null>(null);
  const [openFilterDropdown, setOpenFilterDropdown] = useState<ToolbarFilterDropdownId | null>(
    null,
  );
  const showProjectFilter =
    selectedProjects !== undefined &&
    onProjectsChange !== undefined &&
    projectOptions !== undefined;
  const showStatusFilter =
    selectedStatuses !== undefined && onStatusesChange !== undefined && statusOptions !== undefined;
  const statusOptionsWithIcons = useMemo(
    () => (statusOptions ? withStatusFilterOptionIcons(statusOptions) : []),
    [statusOptions],
  );
  const showSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const showEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  const showItemFilter =
    selectedItems !== undefined && onItemsChange !== undefined && itemOptions !== undefined;
  const dateOptionValues = new Set(dateQuickFilterOptions);
  const dateOptions = [
    ...defaultDateDropdownOptions.filter((option) => {
      if (option.value === 'latest' && !includeLatest) return false;
      return dateQuickFilterOptions === undefined || dateOptionValues.has(option.value);
    }),
    ...(showCustomDateRange
      ? [{ value: 'custom', label: 'Custom' } satisfies DateDropdownOption]
      : []),
  ];
  const selectedDateRange =
    hasCustomDates && !isLatestMode
      ? ['custom']
      : dateOptions.some((option) => option.value === range)
        ? [range]
        : [];
  const dateLabel =
    selectedDateRange[0] === 'custom'
      ? 'Custom'
      : (dateOptions.find((option) => option.value === range)?.label ?? 'Date');
  const defaultRange = '7d';
  const hasDirtyToolbarState =
    searchValue.trim().length > 0 ||
    selectedTags.length > 0 ||
    (selectedStatuses?.length ?? 0) > 0 ||
    (selectedProjects?.length ?? 0) > 0 ||
    (selectedSuites?.length ?? 0) > 0 ||
    (selectedEntities?.length ?? 0) > 0 ||
    (selectedItems?.length ?? 0) > 0 ||
    hasCustomDates ||
    range !== defaultRange;
  const resetToolbar = (): void => {
    if (!hasDirtyToolbarState) return;
    onSearchChange('');
    onTagsChange([]);
    onStatusesChange?.([]);
    onProjectsChange?.([]);
    onSuitesChange?.([]);
    onEntitiesChange?.([]);
    onItemsChange?.([]);
    onSelectedStartDayChange(null);
    onSelectedEndDayChange(null);
    onRangeChange(defaultRange);
  };
  const formattedSummaryStartDay = formatSummaryDay(summaryStartDay);
  const formattedSummaryEndDay = formatSummaryDay(summaryEndDay);
  const summaryDayCount = getSummaryDayCount(summaryStartDay, summaryEndDay);
  const summaryEntityLabel = summaryCount === 1 ? summarySingularLabel : summaryPluralLabel;
  const summaryRelatedEntityText =
    summaryRelatedEntityLabel && summaryRelatedEntityLabel.trim().length > 0
      ? ` for ${summaryRelatedEntityLabel.trim()}`
      : '';
  const summaryComparisonText =
    !isLatestMode && summaryDayCount !== null
      ? ` Comparing against the previous ${formatComparisonPeriod(summaryDayCount)} period.`
      : '';
  const summaryText =
    summaryCount === 0 && summaryEmptyText !== null && summaryEmptyText.trim().length > 0
      ? summaryEmptyText.trim()
      : summaryCount !== null && formattedSummaryStartDay && formattedSummaryEndDay
        ? `Showing ${summaryCount.toLocaleString()} ${summaryEntityLabel}${summaryRelatedEntityText} between ${formattedSummaryStartDay} - ${formattedSummaryEndDay}.${summaryComparisonText}`
        : null;
  const hasEmptySummaryText = summaryCount === 0 && summaryText !== null;
  const summaryDatePending = isLatestMode && summaryCount !== null && summaryText === null;
  const summaryCountPending =
    summaryCount === null && formattedSummaryStartDay !== null && formattedSummaryEndDay !== null;
  const showSummarySkeleton =
    !hasEmptySummaryText && (summaryLoading || summaryDatePending || summaryCountPending);
  const showSummarySlot = showSummarySkeleton || summaryText !== null;
  const getFilterOpenProps = (
    id: ToolbarFilterDropdownId,
  ): { open: boolean; onOpenChange: (open: boolean) => void } => ({
    open: openFilterDropdown === id,
    onOpenChange: (nextOpen) => {
      setOpenFilterDropdown((currentOpenDropdown) => {
        if (nextOpen) return id;
        return currentOpenDropdown === id ? null : currentOpenDropdown;
      });
    },
  });

  return (
    <Box
      sx={{
        width: '100%',
        mb: showSummarySlot ? `calc(-1 * ${summarySlotBottomCompensation})` : undefined,
      }}
    >
      <Box
        aria-label="Next page toolbar preview"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: s('lg'),
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: s('lg'),
            flexWrap: 'wrap',
            minWidth: 0,
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 220, md: 260 },
              minWidth: { xs: '100%', sm: 220 },
              flex: { xs: '1 1 100%', sm: '0 1 260px' },
            }}
          >
            <SearchFilterField
              value={searchValue}
              inputAriaLabel={searchAriaLabel}
              compact
              fullWidth
              controlHeight={toolbarControlHeight}
              leadingSearchAdornment
              trailingSearchAdornment={false}
              placeholder="Search..."
              onChange={(event) => {
                onSearchChange(event.target.value);
              }}
              sx={nextToolbarControlSx}
            />
          </Box>
          {showSuiteFilter ? (
            <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label={suiteLabel}
                inputAriaLabel={`Filter by ${suiteLabel.toLowerCase()}`}
                value={selectedSuites}
                options={suiteOptions}
                onChange={onSuitesChange}
                emptyLabel={toToolbarEmptyLabel(suiteLabel)}
                fullWidth
                showLabel={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('suite')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          {showEntityFilter ? (
            <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label={entityLabel}
                inputAriaLabel={`Filter by ${entityLabel.toLowerCase()}`}
                value={selectedEntities}
                options={entityOptions}
                onChange={onEntitiesChange}
                emptyLabel={toToolbarEmptyLabel(entityLabel)}
                fullWidth
                showLabel={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('entity')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          {showItemFilter ? (
            <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label={itemLabel}
                inputAriaLabel={`Filter by ${itemLabel.toLowerCase()}`}
                value={selectedItems}
                options={itemOptions}
                onChange={onItemsChange}
                emptyLabel={toToolbarEmptyLabel(itemLabel)}
                fullWidth
                showLabel={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('item')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          {showProjectFilter ? (
            <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label={getFilterLabel('project')}
                inputAriaLabel="Filter by project"
                value={selectedProjects}
                options={projectOptions}
                onChange={onProjectsChange}
                emptyLabel="Projects"
                fullWidth
                showLabel={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('project')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          {showStatusFilter ? (
            <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label={getFilterLabel('status')}
                inputAriaLabel="Filter by status"
                value={selectedStatuses}
                options={statusOptionsWithIcons}
                onChange={onStatusesChange}
                emptyLabel="Statuses"
                fullWidth
                showLabel={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('status')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          <Box sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
            <TagsFilterField
              label={getFilterLabel('tags')}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={
                tagOptionsLoading ? getFilterEmptyLabel('loadingTags') : getFilterLabel('tags')
              }
              fullWidth
              showLabel={false}
              fontSizePx={toolbarControlFontSize}
              optionFontSizePx={toolbarPopoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('xs')}
              controlHeight={toolbarControlHeight}
              controlRadius={toolbarControlRadius}
              showClearButton={false}
              optionPresentation="tagChip"
              {...getFilterOpenProps('tags')}
              inputLeftPadding={nextFilterInputLeftPadding}
              inputBackgroundColor={nextFilterBackgroundColor}
              dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
            />
          </Box>
          <Box ref={dateControlRef} sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
            <TagsFilterField
              label="Date"
              inputAriaLabel="Filter by date"
              value={selectedDateRange}
              defaultValue={[defaultRange]}
              options={dateOptions}
              onChange={(nextRange) => {
                if (nextRange.length === 0) return;
                const nextValue = nextRange[nextRange.length - 1];
                if (nextValue === 'custom') return;
                onRangeChange(nextValue as typeof range);
              }}
              onOptionSelect={(optionValue) => {
                if (optionValue !== 'custom') return undefined;
                onCustomDateRangeOpen?.(dateControlRef.current);
                return false;
              }}
              emptyLabel={dateLabel}
              fullWidth
              showLabel={false}
              multiple={false}
              fontSizePx={toolbarControlFontSize}
              optionFontSizePx={toolbarPopoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('xs')}
              controlHeight={toolbarControlHeight}
              controlRadius={toolbarControlRadius}
              showClearButton={false}
              {...getFilterOpenProps('date')}
              inputLeftPadding={nextFilterInputLeftPadding}
              inputBackgroundColor={nextFilterBackgroundColor}
              dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<ReplayOutlinedIcon />}
            fullWidthOnMobile={false}
            onClick={resetToolbar}
            disabled={!hasDirtyToolbarState}
            sx={{
              minHeight: toolbarControlHeight,
              height: toolbarControlHeight,
              borderRadius: toolbarControlRadius,
              px: s('lg'),
              backgroundColor: 'background.paper',
            }}
          >
            Reset
          </Button>
        </Box>
        {showSummarySlot ? (
          <Box
            sx={{
              minHeight: (theme) =>
                `calc(${theme.typography.pxToRem(16)} * 1.4 + ${summarySlotOffset})`,
              display: 'flex',
              alignItems: 'center',
              pt: summarySlotOffset,
              width: '100%',
            }}
          >
            {showSummarySkeleton ? (
              <Skeleton
                variant="rounded"
                width={summarySkeletonWidth}
                height={14}
                sx={{ borderRadius: r('pill') }}
              />
            ) : summaryText ? (
              <Text
                role="bodyMuted"
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(16),
                  lineHeight: 1.4,
                  textAlign: 'left',
                  maxWidth: '100%',
                }}
              >
                {summaryText}
              </Text>
            ) : null}
          </Box>
        ) : null}
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '& .MuiButton-root': {
                minHeight: toolbarControlHeight,
                height: toolbarControlHeight,
              },
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
